import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    findParentNode,
    findSiblings,
} from 'components/WorkspaceEditor/features/promoteDemote/utils/nodeHelpers'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { TrackedChange } from 'models/changes'
import { AddPropertyErrorMessage } from 'pages/ProjectsPage/WorkspacePage/components/Common/addUtils'
import type {
    ValidationContext,
    ValidationResult,
    ValidationStrategy,
} from '../types'
import {
    extractParagraphKey,
    isDuplicateArticle,
    isDuplicateParagraphKey,
} from '../utils/validationHelpers'
import {
    groupTrackChangesByParagraph,
    simulatePostActionElements,
} from './trackChangeSimulation'

const VALID: ValidationResult = { isValid: true }

/** Blocks duplicate article names (skips non-Article nodes). */
export const articleDuplicateValidator: ValidationStrategy = {
    validate: (context: ValidationContext): ValidationResult => {
        const {
            text,
            textElement,
            isAddOperation,
            contentNodes = [],
            nodeType,
        } = context

        if (nodeType !== 'Article') return VALID

        const filteredNodes = isAddOperation
            ? contentNodes
            : contentNodes.filter((node) =>
                  node.elements?.every((e) => e.id !== textElement.id)
              )

        const isDuplicate = isDuplicateArticle(text, filteredNodes)

        return isDuplicate
            ? {
                  isValid: false,
                  errorMessage: `Duplicate Article: "${text}" already exists. Please choose a different name.`,
              }
            : VALID
    },
}

/** Blocks duplicate paragraph keys at the same level (all workspaces).
 *  For UPDATE, extracts the key from the post-edit elements so a
 *  non-key fragment edit that shifts the effective key is caught. */
export const paragraphKeyDuplicateValidator: ValidationStrategy = {
    validate: (context: ValidationContext): ValidationResult => {
        const {
            text,
            textElement,
            isAddOperation,
            contentNodes = [],
            parentNode,
        } = context

        if (!parentNode) return VALID

        const containerNode = isAddOperation
            ? undefined
            : contentNodes.find((node) =>
                  node.elements?.some((e) => e.id === textElement.id)
              )

        const effectiveElements = containerNode?.elements
            ? containerNode.elements.map((el) =>
                  el.id === textElement.id ? { ...el, text } : el
              )
            : [{ text }]

        const keyFromText = extractParagraphKey(effectiveElements)
        if (!keyFromText) return VALID

        const siblings = findSiblings(contentNodes, parentNode, containerNode?.id)
        const isDuplicate = isDuplicateParagraphKey(keyFromText, siblings)

        return isDuplicate
            ? { isValid: false, errorMessage: AddPropertyErrorMessage }
            : VALID
    },
}

export interface TrackChangeValidationContext {
    trackChanges: TrackedChange[]
    /** True for accept/acceptAll, false for reject/discardAll. */
    isAccept: boolean
    contentNodes: Partial<ContentItemViewNodesType>[]
    isContentTeamFromStudioPage: boolean
}

/**
 * OM-only pre-check for a track-change accept/reject. For every paragraph
 * the user is acting on, builds the elements that paragraph WILL have once
 * the change lands, extracts the resulting key, and compares it to each
 * sibling's resulting key. Sibling keys are computed from their own
 * post-action elements too, so a bulk action that would simultaneously
 * collapse two paragraphs onto the same key is still caught.
 *
 * Returns `{ isValid: false }` when the action would create a duplicate;
 * the caller (`useHandleCardActions`) blocks the mutation on that result.
 */
export const validateTrackChangeAction = (
    ctx: TrackChangeValidationContext
): ValidationResult => {
    if (!ctx.isContentTeamFromStudioPage) return VALID
    if (!ctx.trackChanges?.length) return VALID

    const tcsByParagraph = groupTrackChangesByParagraph(ctx.trackChanges)
    if (tcsByParagraph.size === 0) return VALID

    const postActionByParagraph = new Map<string, Partial<TextContentElement>[]>()
    ctx.contentNodes.forEach((node) => {
        if (!node.id || !node.elements) return
        const tcs = tcsByParagraph.get(node.id)
        const simulated = tcs
            ? simulatePostActionElements(node.elements, tcs, ctx.isAccept)
            : node.elements
        postActionByParagraph.set(node.id, simulated)
    })

    for (const [contentItemId] of tcsByParagraph) {
        const paragraph = ctx.contentNodes.find((n) => n.id === contentItemId)
        if (!paragraph) continue

        const newElements = postActionByParagraph.get(contentItemId) ?? []
        const newKey = extractParagraphKey(newElements)
        if (!newKey) continue

        const parentNode = findParentNode(ctx.contentNodes, paragraph)
        if (!parentNode) continue

        const siblings = findSiblings(
            ctx.contentNodes,
            parentNode,
            paragraph.id
        )
        const syntheticSiblings = siblings.map((s) => ({
            ...s,
            elements:
                postActionByParagraph.get(s.id ?? '') ?? s.elements ?? [],
        }))

        if (isDuplicateParagraphKey(newKey, syntheticSiblings)) {
            return {
                isValid: false,
                errorMessage: AddPropertyErrorMessage,
            }
        }
    }

    return VALID
}
